// ============================================================
//  BrandingCard.js — Website Widget → widget settings → "Remove branding"
//  Hide "Powered by CommandKit" from one widget. Paid monthly from the
//  CommandKit wallet, with auto-renew (cron/wallet_autorenew.php).
// ============================================================
import './BrandingCard.css';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { buyWidgetBranding, widgetBrandingRenew, widgetBrandingTag } from '../../api/client';

const naira = n => `₦${Number(n || 0).toLocaleString('en-NG', { maximumFractionDigits: 2 })}`;
const day = s => (s ? new Date(String(s).replace(' ', 'T')).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const newNonce = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

export default function BrandingCard({ widget, price, onUpdated }) {
  const b = widget.branding || {};
  const lapsed = !b.removed && !!b.until;
  const [confirm, setConfirm] = useState(null);   // nonce while asking "pay ₦X?"
  const [busy, setBusy] = useState('');
  const [short, setShort] = useState('');         // "not enough in your wallet" message
  // What shows instead once branding is removed: nothing, or the owner's own tag
  const [mode, setMode] = useState(b.mode || 'none');
  const [tagText, setTagText] = useState(b.text || '');
  const [tagLink, setTagLink] = useState(b.link || '');
  useEffect(() => { setMode(b.mode || 'none'); setTagText(b.text || ''); setTagLink(b.link || ''); }, [widget.wId, b.mode, b.text, b.link]);
  const tagDirty = mode !== (b.mode || 'none') || (mode === 'custom' && (tagText.trim() !== (b.text || '') || tagLink.trim() !== (b.link || '')));

  async function saveTag() {
    if (mode === 'custom' && !tagText.trim()) { toast.error('Type the words for your tag, e.g. “Powered by WaPay”'); return; }
    setBusy('tag');
    try {
      const res = await widgetBrandingTag(widget.wId, mode, tagText.trim(), tagLink.trim());
      onUpdated(res.data.widget);
      toast.success(res.data.message);
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not save'); }
    setBusy('');
  }

  async function pay() {
    if (!confirm) { setConfirm(newNonce()); setShort(''); return; }
    setBusy('pay');
    try {
      const res = await buyWidgetBranding(widget.wId, confirm);
      onUpdated(res.data.widget);
      toast.success(res.data.message, { icon: '✨' });
      setConfirm(null);
    } catch (e) {
      const d = e?.response?.data || {};
      if (d.code === 'insufficient') setShort(d.message);
      else toast.error(d.message || 'Payment failed — nothing was charged');
      setConfirm(null);
    }
    setBusy('');
  }

  async function renew(on) {
    setBusy('renew');
    try {
      const res = await widgetBrandingRenew(widget.wId, on);
      onUpdated(res.data.widget);
      toast.success(on ? 'We’ll renew it from your wallet each month' : 'Auto-renew off — branding returns after ' + day(b.until));
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not update'); }
    setBusy('');
  }

  return (
    <section className={`wwg-card bdc ${b.removed ? 'is-on' : ''}`}>
      <div className="bdc-head">
        <span className="bdc-ic"><i className="fa-solid fa-wand-magic-sparkles" /></span>
        <div>
          <h3>Remove branding</h3>
          <p className="wwg-sub">Hide “Powered by CommandKit” so the chat looks 100% yours.</p>
        </div>
        <span className="bdc-price">{naira(price)}<small>/month</small></span>
      </div>

      <div className="bdc-demo" aria-hidden="true">
        <span className="bdc-demo-box"><i className="fa-regular fa-message" /> Type a message…</span>
        {!b.removed
          ? <span className="bdc-demo-pw">Powered by <b>CommandKit</b></span>
          : mode === 'custom' && tagText.trim()
            ? <span className={`bdc-demo-pw is-own ${tagLink.trim() ? 'is-link' : ''}`}>{tagText.trim()}</span>
            : <span className="bdc-demo-pw is-empty">No tag — clean footer</span>}
      </div>

      {b.removed ? (
        <>
          <div className="bdc-status">
            <i className="fa-solid fa-circle-check" />
            <span><b>Branding removed</b> · paid until {day(b.until)}</span>
          </div>
          <div className="bdc-tag">
            <b>Show instead</b>
            <div className="bdc-seg" role="radiogroup" aria-label="What shows in the chat footer">
              <button type="button" role="radio" aria-checked={mode === 'none'} className={mode === 'none' ? 'is-on' : ''} onClick={() => setMode('none')}>
                <i className="fa-solid fa-ban" /> No tag
              </button>
              <button type="button" role="radio" aria-checked={mode === 'custom'} className={mode === 'custom' ? 'is-on' : ''} onClick={() => setMode('custom')}>
                <i className="fa-solid fa-tag" /> My own tag
              </button>
            </div>
            {mode === 'custom' && (
              <div className="bdc-fields">
                <label className="wwg-field">
                  <span>Tag text</span>
                  <input value={tagText} onChange={e => setTagText(e.target.value)} maxLength={60} placeholder={`Powered by ${widget.label || 'your brand'}`} />
                </label>
                <label className="wwg-field">
                  <span>Link (optional)</span>
                  <input value={tagLink} onChange={e => setTagLink(e.target.value)} maxLength={300} placeholder="https://yourwebsite.com" inputMode="url" />
                </label>
              </div>
            )}
            {tagDirty && (
              <button type="button" className="wwg-btn wwg-btn-brand wwg-btn-sm bdc-save" onClick={saveTag} disabled={busy === 'tag'}>
                {busy === 'tag' ? <span className="wwg-spin" /> : <i className="fa-solid fa-check" />} Save footer
              </button>
            )}
          </div>

          <div className="bdc-row">
            <div><b>Renew automatically</b><span>{b.autoRenew ? `${naira(price)} from your wallet on ${day(b.until)}` : `Branding comes back on ${day(b.until)}`}</span></div>
            <button type="button" role="switch" aria-checked={b.autoRenew} aria-label="Renew automatically" disabled={busy === 'renew'}
              className={`wwg-switch ${b.autoRenew ? 'is-on' : ''}`} onClick={() => renew(!b.autoRenew)}><span /></button>
          </div>
          <button type="button" className={`wwg-btn wwg-btn-ghost wwg-btn-sm bdc-more ${confirm ? 'is-ask' : ''}`} onClick={pay} disabled={busy === 'pay'} onBlur={() => setConfirm(null)}>
            {busy === 'pay' ? <span className="wwg-spin" /> : <i className="fa-regular fa-calendar-plus" />}
            {confirm ? ` Tap again to pay ${naira(price)} for one more month` : ' Pay another month in advance'}
          </button>
        </>
      ) : (
        <>
          {lapsed && <div className="bdc-note"><i className="fa-solid fa-rotate-left" /> Branding came back on {day(b.until)}. Remove it again any time.</div>}
          <button type="button" className={`wwg-btn bdc-buy ${confirm ? 'is-ask' : ''}`} onClick={pay} disabled={busy === 'pay'} onBlur={() => setConfirm(null)}>
            {busy === 'pay' ? <span className="wwg-spin" /> : <i className={`fa-solid ${confirm ? 'fa-wallet' : 'fa-wand-magic-sparkles'}`} />}
            {confirm ? ` Tap again to pay ${naira(price)} from your wallet` : ` Remove branding · ${naira(price)}/month`}
          </button>
          <p className="bdc-fine">Paid from your CommandKit wallet and renewed monthly — switch auto-renew off any time. Everything else stays free.</p>
        </>
      )}

      {short && (
        <div className="bdc-short" role="alert">
          <span><i className="fa-solid fa-wallet" /> {short}</span>
          <Link className="wwg-btn wwg-btn-dark wwg-btn-sm" to="/wallet">Top up wallet</Link>
        </div>
      )}
    </section>
  );
}
